import * as React from 'react';
import { AppContext } from '../../../App';
import { DialogContentText, Tooltip, IconButton } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useTheme } from '@mui/material/styles';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DialogGlynt from '../../common/Dialog/DialogGlynt';
import DialogTitleGlynt from '../../common/Dialog/DialogTitleGlynt';
import DialogContentGlynt from '../../common/Dialog/DialogContentGlynt';
import DialogActionsGlynt from '../../common/Dialog/DialogActionsGlynt';
import { useCreateFromPackage } from '../../hooks/packages/useCreateFromPackage';
import { AxiosError } from 'axios';
import { extractApiError } from '../../../utils/extractApiError';
import { packagesRefreshUntilAtom } from '../../../atoms/packager-atoms';

// Long enough for the background task to create and render the new package.
const REFRESH_WINDOW_MS = 2 * 60 * 1000;

export interface IPackageCreateFromProps {
  packageId: string;
}

const PackageCreateFrom: React.FC<IPackageCreateFromProps> = ({ packageId }) => {
  const [open, setOpen] = React.useState(false);

  const { setToast } = React.useContext(AppContext);
  const queryClient = useQueryClient();
  const theme = useTheme();
  const setRefreshUntil = useSetAtom(packagesRefreshUntilAtom);

  const mutationCreateFrom = useCreateFromPackage({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['packager'] });
      queryClient.invalidateQueries({ queryKey: ['packages'] });
      queryClient.invalidateQueries({ queryKey: ['package'] });
      setRefreshUntil(Date.now() + REFRESH_WINDOW_MS);
      setToast({
        variant: 'success',
        open: true,
        message: 'New package started. It will appear as a DRAFT once rendered.',
      });
    },
    onError: (error: AxiosError) => {
      setToast({
        variant: 'error',
        open: true,
        message: extractApiError(error, 'Failed to create package'),
      });
    },
  });

  const handleClickOpen = () => setOpen(true);
  const handleCloseConfirmation = () => setOpen(false);

  const handleCreate = () => {
    mutationCreateFrom.mutate(packageId);
    setOpen(false);
  };

  return (
    <>
      <Tooltip
        placement="top"
        title={'Create New Package From This One'}
        disableFocusListener
        disableTouchListener
      >
        <IconButton
          data-cy={`btn-create-from-package-${packageId}`}
          onClick={!mutationCreateFrom.isPending ? handleClickOpen : undefined}
          disabled={mutationCreateFrom.isPending}
          sx={{
            color: theme.palette.custom.lightGreen,
            '&:hover': {
              backgroundColor: theme.palette.custom.lightGreen + '20',
            },
          }}
        >
          <ContentCopyIcon fontSize="medium" />
        </IconButton>
      </Tooltip>
      <DialogGlynt
        open={open}
        closeModal={handleCloseConfirmation}
        loading={mutationCreateFrom.isPending}
        cypressId="dialog-package-create-from"
      >
        <DialogTitleGlynt
          title="Create package from this one"
          closeModal={handleCloseConfirmation}
          loading={mutationCreateFrom.isPending}
        />
        <DialogContentGlynt>
          <DialogContentText>
            This creates a new package containing the same documents, rendered against the latest
            data. Your include and exclude choices are carried over.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            This package is left exactly as it is. The new one starts as a DRAFT.
          </DialogContentText>
        </DialogContentGlynt>
        <DialogActionsGlynt
          handleSubmit={handleCreate}
          closeModal={handleCloseConfirmation}
          cancelCypressId="btn-package-create-from-modal-cancel"
          submitCypressId="btn-package-create-from-modal-yes"
          clearCypressId=""
          submitButtonText="Create"
          submitLoading={mutationCreateFrom.isPending}
          showClearButton={false}
        />
      </DialogGlynt>
    </>
  );
};

export default PackageCreateFrom;
